import { useState, useEffect } from "react";
import { EBOOK_PARTS } from "../data/ebook";

export default function ModelPage() {
  const [activePart, setActivePart] = useState(EBOOK_PARTS[0].id);

  useEffect(() => {
    // Scroll to hash target on load
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, []);

  return (
    <div data-testid="model-page">
      {/* Header */}
      <section className="border-b border-[#1A1915]/15">
        <div className="max-w-[1400px] mx-auto px-6 py-16 lg:py-24">
          <div className="eyebrow">The Full Model</div>
          <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#1A1915] leading-[0.95] max-w-4xl">The AuthorityOS Model — <span className="italic">an e-book</span></h1>
          <p className="mt-8 max-w-3xl text-lg text-[#1A1915] leading-relaxed">
            Four parts. Nineteen sections. Every one written to be actionable — three blocks per section: what it is, how it shows up day to day, and specific practices. Read straight through, or use the navigation to jump.
          </p>
        </div>
      </section>

      {/* Sticky part nav */}
      <nav className="sticky top-[65px] z-30 border-b border-[#1A1915]/15 bg-[#FAFAF9]/95 backdrop-blur-md" data-testid="ebook-nav">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex flex-wrap gap-2">
          {EBOOK_PARTS.map((p) => (
            <a
              key={p.id}
              href={`#part-${p.id}`}
              onClick={() => setActivePart(p.id)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border ${activePart === p.id ? "bg-[#1A1915] text-[#FAFAF9] border-[#1A1915]" : "border-[#1A1915]/25 text-[#1A1915] hover:bg-[#F5F5F4]"}`}
              data-testid={`ebook-nav-${p.id}`}
            >
              {p.label} · {p.title}
            </a>
          ))}
        </div>
      </nav>

      {/* Reader */}
      <div className="max-w-[900px] mx-auto px-6 py-20 space-y-24">
        {EBOOK_PARTS.map((part) => (
          <section key={part.id} id={`part-${part.id}`}>
            <div className="border-t-4 border-[#1A1915] pt-8">
              <div className="eyebrow text-[#B8863C]">{part.label}</div>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">{part.title}</h2>
              {part.intro && (
                <p className="mt-6 text-base text-[#78716C] italic leading-relaxed">{part.intro}</p>
              )}
            </div>

            <div className="mt-12 space-y-16">
              {part.sections.map((sec) => (
                <article key={sec.id} id={sec.id} data-testid={`ebook-section-${sec.id}`}>
                  <h3 className="font-serif text-3xl text-[#1A1915] leading-tight">{sec.title}</h3>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 border-l-2 border-[#1A1915] pl-6">
                    <div className="md:col-span-3 eyebrow">What it is</div>
                    <p className="md:col-span-9 text-base text-[#1A1915] leading-relaxed">{sec.what}</p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 border-l-2 border-[#78716C] pl-6">
                    <div className="md:col-span-3 eyebrow">How it shows up</div>
                    <p className="md:col-span-9 text-base text-[#1A1915] leading-relaxed">{sec.daily}</p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 border-l-2 border-[#B8863C] pl-6">
                    <div className="md:col-span-3 eyebrow text-[#B8863C]">Practices</div>
                    <ul className="md:col-span-9 space-y-3">
                      {sec.practices.map((p, i) => (
                        <li key={i} className="text-base text-[#1A1915] leading-relaxed flex gap-3">
                          <span className="text-[#B8863C] font-serif text-xl leading-none">·</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
